import {useRef, useState} from 'react'
import {Pressable, StyleSheet, View} from 'react-native'
import {Image} from 'expo-image'
import {useInfiniteScroll, useRequest} from 'ahooks'
import {format} from 'date-fns'

import displayNumber from '#/lib/displayNumber'
import {useGoBack} from '#/lib/hooks/useGoBack'
import PointsEmpty from '#/screens/PointsRecord/PointsEmpty'
import {SendPointsDialog} from '#/screens/Profile/Header/SendPointsDialog'
import {atoms as a, useTheme} from '#/alf'
import * as Dialog from '#/components/Dialog'
import * as Layout from '#/components/Layout'
import {Text} from '#/components/Typography'
import server from '#/server'
import {type GrainKind, type GrainTransfer} from '#/server/rice/types'

type InfiniteScrollType = {
  list: GrainTransfer[]
  /** rice 是游标分页,没有总数;null 表示还有下一页 */
  cursor: string | null
}

const PAGE_SIZE = 30

function getGrainKindLabel(kind: GrainKind) {
  switch (kind) {
    case 'reward':
      return '赞赏'
    case 'gift':
      return '赠送'
    case 'grant':
      return '后台发放'
    default:
      return '未知'
  }
}

const PointsRecordScreen = () => {
  const t = useTheme()
  const goBack = useGoBack()
  const sendControl = Dialog.useDialogControl()

  const ref = useRef(null)
  const [selectedToUserId, setSelectedToUserId] = useState<string>()
  const [selectedToUserDomainName, setSelectedToUserDomainName] =
    useState<string>()

  const {data: userDetail} = useRequest(() =>
    server.rice.getCurrentUser(),
  )

  const {
    data: pointsRecord,
    loading,
    loadMore,
    loadingMore,
  } = useInfiniteScroll<InfiniteScrollType>(d => getPoints(d), {
    target: ref,
    isNoMore: d => !d?.cursor,
  })

  const getPoints = async (d?: InfiniteScrollType) => {
    const page = await server.rice.listGrainTransfers({
      limit: PAGE_SIZE,
      before: d?.cursor ?? undefined,
    })

    return {
      list: (d?.list || []).concat(page.data),
      cursor: page.meta.next_cursor,
    }
  }

  return (
    <Layout.Screen testID="PointsRecordScreen">
      <Layout.Center style={styles.container}>
        <Pressable
          accessibilityRole="button"
          accessibilityIgnoresInvertColors
          style={{position: 'absolute', left: 16, top: 18, zIndex: 1}}
          onPress={goBack}>
          <Image
            accessibilityIgnoresInvertColors
            style={{width: 14, height: 12}}
            source={require('#/assets/arrow-left.svg')}
          />
        </Pressable>

        <View style={[styles.headContainer, a.pb_lg]}>
          <View style={styles.headImage}>
            <Image
              accessibilityIgnoresInvertColors
              style={{width: '100%', height: 346}}
              source={require('#/assets/points/bg.png')}
            />
          </View>
          <View style={[styles.headPlaceholder, a.mb_lg]} />
          <View style={[a.px_lg]}>
            <Text style={[t.atoms.text_contrast_low, a.text_sm]}>我的稻米</Text>
          </View>
          <View style={[a.px_lg, a.mt_md]}>
            <Text
              style={[
                t.atoms.text,
                a.text_4xl,
                a.text_family_ddin,
                a.font_bold,
              ]}>
              {displayNumber(userDetail?.grain_balance)}
            </Text>
          </View>
        </View>
        <View style={styles.card}>
          <Text style={[{fontWeight: 500, color: '#0B0F14', marginBottom: 18}]}>
            明细
          </Text>
          <View style={[styles.card_content, a.overflow_auto]} ref={ref}>
            {pointsRecord?.list.length === 0 ? (
              <PointsEmpty />
            ) : (
              pointsRecord?.list.map((p, index) => {
                // rice 一笔转账只有一行,收付靠 direction 区分;
                // core 是收付各记一行、金额带符号。
                const isPositive = p.direction === 'in'
                const counterparty = isPositive ? p.from : p.to
                return (
                  <View
                    key={p.id}
                    style={[
                      a.flex_row,
                      a.justify_between,
                      a.px_lg,
                      a.align_center,
                      styles.item,
                    ]}>
                    <View style={[a.flex_col, a.justify_between, a.gap_sm]}>
                      <Text
                        style={[
                          a.text_md,
                          a.font_bold,
                          t.atoms.text_contrast_high,
                        ]}>
                        {`${getGrainKindLabel(p.kind)} ${
                          counterparty?.nickname || counterparty?.handle || ''
                        }`}
                      </Text>
                      {p.memo ? (
                        <Text
                          style={[
                            t.atoms.text_contrast_high,
                            {color: '#F66455'},
                          ]}>
                          附言：{p.memo}
                        </Text>
                      ) : null}
                      <Text style={[t.atoms.text_contrast_low]}>
                        {format(
                          new Date(p.inserted_at),
                          'yyyy-MM-dd HH:mm:ss',
                        )}
                      </Text>
                    </View>
                    <View style={[a.flex_col, a.align_end, a.gap_sm]}>
                      <Text
                        style={[
                          a.text_md,
                          a.text_family_ddin,
                          a.font_bold,
                          isPositive ? {color: '#F66455'} : {color: '#37D28C'},
                        ]}>
                        {isPositive ? `+${p.amount}` : `-${p.amount}`}
                      </Text>
                      {counterparty ? (
                        <Pressable
                          accessibilityRole="button"
                          onPress={() => {
                            setSelectedToUserId(counterparty.id)
                            setSelectedToUserDomainName(
                              counterparty.nickname || counterparty.handle,
                            )
                            sendControl.open()
                          }}
                          style={styles.button}>
                          <Text
                            style={[
                              a.text_xs,
                              {color: '#42576C', fontWeight: 500},
                            ]}>
                            {isPositive ? '回赠' : '再次赠送'}
                          </Text>
                        </Pressable>
                      ) : null}
                    </View>
                  </View>
                )
              })
            )}
          </View>
        </View>
      </Layout.Center>
      <SendPointsDialog
        control={sendControl}
        defaultToUserId={selectedToUserId}
        defaultToUserDomainName={selectedToUserDomainName}
        onUpdate={() => {}}
      />
    </Layout.Screen>
  )
}

export default PointsRecordScreen

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'hidden',
  },
  headContainer: {
    position: 'relative',
    height: 150,
  },
  headImage: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: '100%',
  },

  headPlaceholder: {
    height: 52,
  },

  card: {
    flex: 1,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#F1F3F5',
    backgroundImage: 'linear-gradient(180deg, #F3F9FE 0%, #FBFDFF 100%)',
    paddingBlock: 18,
    paddingInline: 16,
  },
  card_content: {
    height: 'calc(100% - 50px)',
  },
  item: {
    width: '100%',
    backgroundColor: '#fff',
    borderRadius: 12,
    marginBottom: 12,
    paddingBlock: 10,
  },
  button: {
    backgroundColor: '#F1F3F5',
    borderRadius: 15,
    paddingInline: 12,
    paddingBlock: 6,
  },
})
